/**
 * Moving an element to the end of <body>, so it renders above the app's
 * fixed chrome (the sidebar's menu button, sticky bars) and outside any
 * clipping or transformed ancestor.
 *
 * `moveToBody` is the imperative form: the binder's zoomed card goes to
 * <body> for the zoom and comes back after it, a placeholder holding its
 * place in the grid. `portal` is the action form, for an element that lives
 * in <body> for as long as it is mounted (a modal).
 */

/**
 * Move `node` to the end of <body>. When `placeholder` is given it takes the
 * node's place until the node returns. Returns the function that puts the
 * node back where it was (and removes the placeholder).
 * @param {HTMLElement} node
 * @param {HTMLElement | null} [placeholder]
 * @returns {() => void}
 */
export function moveToBody(node, placeholder = null) {
  const parent = node.parentElement;
  const next = node.nextSibling;
  if (placeholder && parent) parent.insertBefore(placeholder, node);
  document.body.appendChild(node);
  return () => {
    if (placeholder?.parentElement) {
      placeholder.parentElement.insertBefore(node, placeholder);
      placeholder.remove();
    } else if (parent) {
      parent.insertBefore(node, next && next.parentNode === parent ? next : null);
    }
  };
}

/**
 * Svelte action: render the element at the end of <body> while mounted.
 * @param {HTMLElement} node
 */
export function portal(node) {
  document.body.appendChild(node);
  return {
    destroy() {
      node.remove();
    },
  };
}
